import React, { useState, useMemo } from 'react'

import { Text, TextButton, Skeleton, MenuDropdownOptions, MenuOptions } from 'components'

// Utils and Services
import useLeadDetail from 'hooks/use-lead-detail'
import { uuidv4 } from 'utils/generic'
import useContactInfo from 'hooks/use-contact-info'
import useScopeModal from 'hooks/use-scope-modal'

// Types
import { LeadDetails } from 'types/global'

import Accordion from '../AccordionWrapper'
import ContactCard from './ContactCard'
import ContactDetails from './ContactDetails'
import DeactivateContactDetails from './DeactivateContactDetails'
import DeleteContactDetails from './DeleteContactDetails'
import { Actions } from './helper'

export const MODAL_STACK_CONTACT_LIST = 'lead-detail-contact-list' as const

const ContactList: React.FC = () => {
  const { isLoading: isLeadLoading, isFetching: isLeadFetching, canUserEditLeadInfo } = useLeadDetail()
  const { setModal, setCloseModal } = useScopeModal({ stack: MODAL_STACK_CONTACT_LIST })

  const [confirmDialog, setConfirmDialog] = useState<keyof typeof Actions | null>()
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [showInactive, setShowInactive] = useState(false)

  const { data, isSuccess, isLoading, refetch } = useContactInfo({
    queryOptions: {
      enabled: !isLeadLoading && !isLeadFetching,
    },
  })

  const handleContextMenuAction = (success?: boolean) => {
    setSelectedId(null)
    setConfirmDialog(null)

    if (success) {
      refetch()
    }
  }

  const leadContactsData = useMemo(() => {
    if (!data) return []

    if (showInactive) {
      return data
    }

    return data.filter((leadContact: LeadDetails.ContactPerson) => leadContact.isActive)
  }, [data, showInactive])

  const renderLeadContacts = () => {
    const numberOfActiveContacts =
      leadContactsData && leadContactsData.reduce((acc, cur) => (cur.isActive ? acc + 1 : acc), 0)
    return (
      <>
        {leadContactsData &&
          leadContactsData.map((leadContact: LeadDetails.ContactPerson) => {
            const isLastActiveContact = leadContact.isActive && numberOfActiveContacts === 1
            return (
              <ContactCard
                key={leadContact.id}
                canEdit={canUserEditLeadInfo}
                disabled={!canUserEditLeadInfo}
                leadContact={leadContact}
                hideMenu={leadContactsData.length === 1 || isLastActiveContact}
                setConfirmDialog={setConfirmDialog}
                setSelectedId={setSelectedId}
                isLastActiveContact={isLastActiveContact}
              />
            )
          })}
        {canUserEditLeadInfo && (
          <TextButton
            icon="plus"
            mt={16}
            onClick={() =>
              setModal(<ContactDetails onClose={() => setCloseModal('lead-detail-edit-contact-details')} />, {
                id: 'lead-detail-edit-contact-details',
              })
            }
          >
            add_new_contact_btn
          </TextButton>
        )}
      </>
    )
  }

  const accordionOptions: Array<MenuDropdownOptions> = [
    {
      value: 'showDeactivatedContacts',
      label: 'show_deactivated_contacts_lbl',
      icon: showInactive ? 'check' : undefined,
      handleClick: () => setShowInactive(!showInactive),
      iconAlignment: 'right',
    },
  ]

  const AccordionActionsMenu = () => (
    <div onClick={(e) => e.stopPropagation()}>
      <MenuOptions options={accordionOptions} rowWidth={'225px'} />
    </div>
  )

  if (isSuccess) {
    return (
      <>
        <Accordion
          ContextItems={[<AccordionActionsMenu key={uuidv4()} />]}
          displayContextMenu={canUserEditLeadInfo}
          title="contacts_lbl"
        >
          {renderLeadContacts()}
        </Accordion>
        {selectedId && confirmDialog === Actions.deactivate && (
          <DeactivateContactDetails
            contactId={selectedId}
            isActive={Boolean(data?.find((i) => i.id === selectedId)?.isActive)}
            onAction={handleContextMenuAction}
          />
        )}

        {selectedId && confirmDialog === Actions.delete && (
          <DeleteContactDetails contactId={selectedId} onAction={handleContextMenuAction} />
        )}
      </>
    )
  }

  return (
    <Accordion ContextItems={[<AccordionActionsMenu key={uuidv4()} />]} displayContextMenu title="contacts_lbl">
      {isLoading ? <Skeleton width="100%" height="80px" /> : <Text>something_went_wrong_error</Text>}
    </Accordion>
  )
}

export default ContactList
